import { useEffect, useState } from 'react'
import { EventNames } from '../../../types/misc.types'
import { trackEvent } from '../../../utils/appBridge'
import BottomSheetContent from '../BottomSheetContent'
import { PrimaryButton } from '../../execution/PrimaryButton'
import { TertiaryButton } from '../../execution/TertiaryButton'
import RenderHTML from '../../ui-components/RenderHTML'
import { RedirectionTab } from '../../ui-components/RedirectionTab'
import { ExecutionLearnMoreReason } from '../../../types/mf-execution.types'
import { getExecutionLearnMoreConfig } from '../../../utils/mf-execution/learnMoreConfig'

type ExecutionLearnMoreContentProps = {
  onDismiss: () => void
  ScreenName: string
  reason?: ExecutionLearnMoreReason
  directFundISIN: string
  directFundName: string
  fundIsin: string
  fundName: string
}

export const ExecutionLearnMoreContent = ({
  onDismiss,
  ScreenName,
  reason,
  directFundISIN,
  directFundName,
  fundIsin,
  fundName
}: ExecutionLearnMoreContentProps) => {
  const [isNotified, setIsNotified] = useState(false)

  const { icon, heading, content } = getExecutionLearnMoreConfig(reason ?? ExecutionLearnMoreReason.GENERIC)

  useEffect(() => {
    trackEvent({
      eventName: EventNames.BottomSheetLoad,
      payload: {
        eventId: heading,
        bottomSheetName: reason === ExecutionLearnMoreReason.REGULAR ? 'Regular plan error' : 'Learn more',
        ScreenName
      }
    })
  }, [heading, ScreenName])

  const getBottomSheetCTAs = () => {
    if (reason === ExecutionLearnMoreReason.REGULAR) {
      return (
        <PrimaryButton
          testLocator='BS_CTA_PRIMARY'
          label='Close'
          onClick={onDismiss}
          primaryButtonClassName='!h-[56px]'
        />
      )
    }

    if (isNotified)
      return (
        <TertiaryButton onClick={onDismiss} tertiaryButtonClassName='!h-[56px]'>
          <div className='text-[#232323] text-[16px] font-medium leading-[21px] flex items-center gap-3 justify-center'>
            <div className='w-[17px] h-[17px]'>
              <RenderHTML innerHTMLText={BellIcon} />
            </div>
            <span>We will notify you</span>
          </div>
        </TertiaryButton>
      )

    return (
      <PrimaryButton
        primaryButtonClassName='!text-[16px] !h-[56px]'
        label='Notify me'
        onClick={() => {
          trackEvent({
            eventName: EventNames.ButtonTap,
            payload: {
              ScreenName,
              buttonLabel: 'Notify me',
              eventId: heading,
              fundIsin,
              fundName
            }
          })
          setIsNotified(true)
        }}
      />
    )
  }

  return (
    <BottomSheetContent
      logoSrc={icon ?? undefined}
      bottomSheetHeadingTestLocator='BS_TITLE'
      heading={heading}
      bottomSheetHeadingClassName='!font-[500] !leading-[130%]'
      onDismiss={onDismiss}
      bottomSheetCtas={getBottomSheetCTAs()}
    >
      <div>
        <span test-locator='BS_DESC' className='text-[#6A6E6E] leading-[150%] text-[14px] font-[400]'>
          {content}
        </span>
      </div>

      {reason === ExecutionLearnMoreReason.REGULAR && (
        <div className='pt-4'>
          <RedirectionTab isPage={false} directFundISIN={directFundISIN} directFundName={directFundName} title='Invest in Direct Plan' />
        </div>
      )}
    </BottomSheetContent>
  )
}

const BellIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
  <g clip-path="url(#clip0_36453_267249)">
    <path fill-rule="evenodd" clip-rule="evenodd" d="M3.34109 0.934187C3.65358 0.662307 4.12731 0.695231 4.39918 1.00773C4.67108 1.32022 4.63815 1.79395 4.32565 2.06583C3.17087 3.07054 2.38619 4.54127 2.24752 6.22813C2.21358 6.64093 1.85141 6.9481 1.43859 6.91417C1.02577 6.88023 0.718625 6.51807 0.752562 6.10523C0.923611 4.02453 1.89407 2.19314 3.34109 0.934187ZM12.6009 1.00773C12.8728 0.695231 13.3465 0.662307 13.659 0.934187C15.106 2.19314 16.0765 4.02453 16.2475 6.10523C16.2815 6.51807 15.9743 6.88023 15.5615 6.91417C15.1487 6.9481 14.7865 6.64093 14.7526 6.22813C14.6139 4.54127 13.8292 3.07054 12.6744 2.06583C12.3619 1.79395 12.329 1.32022 12.6009 1.00773ZM8.50005 1.66667C11.1394 1.66667 13.3398 3.68653 13.5652 6.3163L13.7249 8.17943C13.7962 9.01113 14.0618 9.81453 14.5002 10.5249L14.9905 11.3195C15.483 12.1174 15.2144 12.9416 14.2849 13.0653C13.2222 13.2066 11.4476 13.3333 8.50005 13.3333C5.55251 13.3333 3.77786 13.2066 2.71513 13.0653C1.78571 12.9416 1.51711 12.1174 2.00953 11.3195L2.49991 10.5249C2.93832 9.81453 3.20385 9.01113 3.27514 8.17943L3.43484 6.3163C3.66025 3.68653 5.86065 1.66667 8.50005 1.66667ZM8.50005 14.3333C7.41641 14.3333 6.48555 14.3164 5.68568 14.2876C6.21251 15.3048 7.27478 16 8.49941 16C9.72401 16 10.7863 15.3049 11.3131 14.2876C10.5135 14.3164 9.58308 14.3333 8.50005 14.3333Z" fill="#232323"/>
  </g>
  <defs>
    <clipPath id="clip0_36453_267249">
      <rect width="16" height="16" fill="white" transform="translate(0.5 0.5)"/>
    </clipPath>
  </defs>
</svg>`
